import { useCallback, useRef } from 'react';

import { useEnableLoyaltyServiceOffers } from 'features/offer/hooks/use-enable-loyalty-service-offers';
import { useEvaluateLoyaltyServiceOffers } from 'features/offer/hooks/use-evaluate-loyalty-service-offers';
import { buildAppliedOfferServiceInputs } from 'features/offer/utils/build-applied-offer-service-inputs';
import { useEnableLoyaltyServiceRewards } from 'features/reward/hooks/use-enable-loyalty-service-rewards';
import { useEvaluateLoyaltyServiceRewards } from 'features/reward/hooks/use-evaluate-loyalty-service-rewards';
import { buildAppliedRewardServiceInputs } from 'features/reward/utils/build-applied-reward-service-inputs';
import { useStoreContext } from 'features/store';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { LoyaltyPaymentMethod } from 'generated/graphql-gateway';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { getServiceModeFromLS } from 'state/service-mode';
import logger from 'utils/logger';

/**
 * Hook to validate, against loyalty rules, the given cart entries
 */
export const useLoyaltyCartEntriesEvaluation = () => {
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const enableRewardCooldown = useFlag(LaunchDarklyFlag.ENABLE_REWARD_COOLDOWN);

  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedLoyaltyOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { evaluateLoyaltyUserOffers, evaluateLoyaltyUserRewards } = useLoyaltyContext();

  // When the loyalty-service offers gate is ON, applied cart offers must be
  // validated against the loyalty service (mirroring `useCartIncentivesEvaluation`),
  // not the legacy `LoyaltyUserOffers` engine query.
  const { enabled: loyaltyServiceOffersEnabled } = useEnableLoyaltyServiceOffers();
  const { evaluateAndApplyOffersFeedback } = useEvaluateLoyaltyServiceOffers();
  // When the loyalty-service rewards gate is ON, applied cart rewards must be
  // validated against the loyalty service (mirroring `useCartIncentivesEvaluation`),
  // not the legacy `LoyaltyUserRewards` engine query.
  const { enabled: loyaltyServiceRewardsEnabled } = useEnableLoyaltyServiceRewards();
  const {
    evaluateAndApplyRewardsFeedback,
    clearRewardsFeedback,
  } = useEvaluateLoyaltyServiceRewards();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const dispatch = useAppDispatch();

  // Only the latest evaluation may clear `offersEvaluating`; a stale run
  // finishing must not re-enable "Continue" while a newer one is in flight.
  const latestOfferEvaluationRef = useRef(0);

  const evaluateLoyaltyCartEntries = useCallback(
    async (
      loyaltyId: string,
      cartEntries?: ILegacyCartEntry[],
      subtotalAmount?: number,
      paymentMethod?: LoyaltyPaymentMethod | null
    ) => {
      const promises: Promise<void>[] = [];

      if (appliedLoyaltyOffers.length) {
        // Gate "Continue" while validating. The ON (loyalty-service) path never
        // feeds the derived `offersLoading`, so bracket both paths with this flag.
        const offerEvaluationId = ++latestOfferEvaluationRef.current;
        dispatch(actions.loyalty.setOffersEvaluating(true));

        const releaseIfCurrent = () => {
          if (offerEvaluationId === latestOfferEvaluationRef.current) {
            dispatch(actions.loyalty.setOffersEvaluating(false));
          }
        };

        try {
          let offerEvaluation: Promise<void>;

          if (loyaltyServiceOffersEnabled) {
            // Mirror the legacy engine's incentive/cart-entry shapes so the service
            // feedback lands in the same Redux map the cart already reads. The
            // stale-guard + feedback dispatch live in `evaluateAndApplyOffersFeedback`.
            const {
              appliedIncentives,
              cartEntries: parsedEntries,
            } = buildAppliedOfferServiceInputs({
              appliedOffers: appliedLoyaltyOffers,
              cartEntries,
              appliedRewards: appliedLoyaltyRewards,
            });

            offerEvaluation = evaluateAndApplyOffersFeedback({
              appliedIncentives,
              cartEntries: parsedEntries,
              serviceMode: getServiceModeFromLS(),
              storeId,
            });
          } else {
            offerEvaluation = evaluateLoyaltyUserOffers(
              loyaltyId,
              appliedLoyaltyOffers,
              cartEntries,
              appliedLoyaltyRewards,
              subtotalAmount,
              paymentMethod
            );
          }

          promises.push(offerEvaluation.finally(releaseIfCurrent));
        } catch (error) {
          // A synchronous throw here happens before any promise exists to
          // attach `.finally()` to, which would otherwise leave
          // `offersEvaluating` — and "Continue" — stuck loading forever.
          logger.error({ error, message: 'Error building/starting loyalty offer evaluation' });
          releaseIfCurrent();
        }
      } else {
        // Release a gate set eagerly at price-start once all offers are removed.
        latestOfferEvaluationRef.current++;
        dispatch(actions.loyalty.setOffersEvaluating(false));
      }

      if (enableRewardUnstacking || enableRewardCooldown) {
        if (Object.values(appliedLoyaltyRewards).length) {
          if (loyaltyServiceRewardsEnabled) {
            // ON: validate rewards against the loyalty service (same map +
            // instance-local stale guard) instead of the legacy engine, so a
            // legacy `isTransactionCreation: true` run can't clobber it.
            const {
              appliedIncentives,
              cartEntries: parsedEntries,
            } = buildAppliedRewardServiceInputs({
              appliedRewards: appliedLoyaltyRewards,
              cartEntries,
              appliedOffers: appliedLoyaltyOffers,
            });

            promises.push(
              evaluateAndApplyRewardsFeedback({
                appliedIncentives,
                cartEntries: parsedEntries,
                serviceMode: getServiceModeFromLS(),
                storeId,
              })
            );
          } else {
            promises.push(
              evaluateLoyaltyUserRewards(
                loyaltyId,
                appliedLoyaltyRewards,
                cartEntries,
                appliedLoyaltyOffers,
                subtotalAmount,
                paymentMethod
              )
            );
          }
        } else if (loyaltyServiceRewardsEnabled) {
          clearRewardsFeedback();
        }
      }

      await Promise.all(promises);
    },
    [
      appliedLoyaltyOffers,
      appliedLoyaltyRewards,
      loyaltyServiceOffersEnabled,
      evaluateAndApplyOffersFeedback,
      loyaltyServiceRewardsEnabled,
      evaluateAndApplyRewardsFeedback,
      clearRewardsFeedback,
      storeId,
      dispatch,
      evaluateLoyaltyUserOffers,
      evaluateLoyaltyUserRewards,
      enableRewardUnstacking,
      enableRewardCooldown,
    ]
  );

  return evaluateLoyaltyCartEntries;
};
